import { useMemo } from 'react';
import { LayoutGrid } from 'lucide-react';
import { SYMPHONY_LOOP_MARKS } from '../../SymphonyMark';
import { connectorLogoUrl } from '../../../lib/connectors';
import { CONNECTORS_PAGE, type AppsController } from '../../../lib/use-connectors';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useAlfredInOutreachEnabled } from '../../../lib/alfred-in-outreach-flag';
import { useTranslation } from '../../../lib/i18n';
import {
  GENOME_ARRIVAL_MS,
  GENOME_BADGE_R,
  GENOME_HUB,
  GENOME_MARK_SIZE,
  GENOME_PULSE_STAGGER_MS,
  GENOME_RINGS,
  GENOME_SHOWN_CHANNELS,
  GENOME_SUPPORTED_CHANNELS,
  GENOME_VIEWBOX,
  genomeCentre,
  genomeSpokePath,
  genomeToolOverflowCount,
  type GenomeSide,
} from '../../../lib/connectors-genome';
import { ClaudeColorGlyph, WhatsAppColorGlyph, WixWordmark } from './BrandGlyphs';
import styles from './ConnectorsGenome.module.css';

/**
 * The Connectors genome — DESKTOP-WEB hero (Figma 14013-149503). A horizontal reflow of the native
 * `ConnectorsHeroView`: the green Symphony hub sits in the CENTRE, the **channels** run down the LEFT
 * and the **tools** down the RIGHT, each wired to the hub, and the signal flows left → right (channels
 * INTO the hub, tools OUT of it). It shares its logic with the vertical mobile-web genome; the two
 * deliberate desktop differences are the horizontal geometry (`lib/connectors-genome.ts`) and native's
 * plate rule — **a connected badge gets a bright white plate, a disconnected one the recessed grey
 * plate**. Like every surface, only the two "+N" chips act — they switch the page to Tools / Channels.
 *
 * The shown badges are a FIXED showcase (native's `channelSpecs`/`toolSpecs`), not the account's
 * arbitrary connectors; the fetched `connectors` drive only the Gmail/Calendar connected state and the
 * tools "+N" count.
 */

type Mark = { glyph?: React.ReactNode; logoUrl?: string; wix?: boolean };

interface HeroNode {
  id: string;
  side: GenomeSide;
  slot: number;
  intoHub: boolean;
  label: string;
  connected: boolean;
  kind: 'badge' | 'overflow';
  mark?: Mark;
  overflowCount?: number;
  overflowTab?: 'tools' | 'channels';
}

export function ConnectorsGenome({
  controller,
  onBrowseTools,
  onBrowseChannels,
}: {
  controller: AppsController;
  onBrowseTools: () => void;
  onBrowseChannels: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();

  const connectors = controller.state.kind === 'ready' ? controller.state.connectors : [];
  const alfredOnOutreach = useAlfredInOutreachEnabled();

  // Hold the whole node reveal until the connector fetch has settled: the fly-in animates once, with
  // final connected-state plates and the tools "+N" chip already present. Animating during `loading`
  // (connectors = []) makes Gmail/Calendar pop grey → colour and the "+N" chip jump in when data lands.
  const dataSettled = controller.state.kind !== 'loading';

  const nodes: HeroNode[] = useMemo(() => {
    const googleCalConnected = connectors.some(
      (c) => (c.calendarProvider?.toUpperCase() === 'GOOGLE' || c.toolkit === 'googlecalendar') && c.connected,
    );

    // Channels (LEFT column). Connected → solid spoke + bright plate: Claude always, WhatsApp reads as
    // reachable, Apps & Web is not a per-account connection. Mirrors native `channelSpecs`.
    const channels: HeroNode[] = [
      { id: 'C0', side: 'left', slot: 0, intoHub: true, label: t('connectors.channel.whatsapp.short'), connected: true, kind: 'badge', mark: { glyph: <g transform="translate(-10,-10)"><WhatsAppColorGlyph size={20} /></g> } },
      { id: 'C1', side: 'left', slot: 1, intoHub: true, label: t('connectors.hero.claude'), connected: true, kind: 'badge', mark: { glyph: <g transform="translate(-11,-11)"><ClaudeColorGlyph size={22} /></g> } },
      { id: 'C2', side: 'left', slot: 2, intoHub: true, label: t('connectors.hero.appsWeb'), connected: false, kind: 'badge', mark: { glyph: <g transform="translate(-10,-10)"><LayoutGrid size={20} /></g> } },
    ];
    const channelOverflow = Math.max(0, GENOME_SUPPORTED_CHANNELS - GENOME_SHOWN_CHANNELS);
    if (channelOverflow > 0) {
      channels.push({ id: 'CX', side: 'left', slot: 3, intoHub: true, label: '', connected: false, kind: 'overflow', overflowCount: channelOverflow, overflowTab: 'channels' });
    }

    // Tools (RIGHT column): the FIXED native set — Gmail, Calendar, Wix — plus the "+N" overflow chip,
    // exactly as native draws it. There is deliberately no substitution for other connected tools: the
    // three slots are always these three. Each shows its own logo; the plate is bright white + the spoke
    // solid when the account has it connected (Gmail / Calendar), grey + dashed when not. Wix always reads
    // connected (native `toolSpecs`).
    const gmailConnected = connectors.some((c) => c.toolkit === 'gmail' && c.connected);
    const tools: HeroNode[] = [
      { id: 'T0', side: 'right', slot: 0, intoHub: false, label: t('connectors.hero.gmail'), connected: gmailConnected, kind: 'badge', mark: { logoUrl: connectorLogoUrl('gmail') } },
      { id: 'T1', side: 'right', slot: 1, intoHub: false, label: t('connectors.hero.calendar'), connected: googleCalConnected, kind: 'badge', mark: { logoUrl: connectorLogoUrl('googlecalendar') } },
      { id: 'T2', side: 'right', slot: 2, intoHub: false, label: t('connectors.hero.wix'), connected: true, kind: 'badge', mark: { wix: true } },
    ];
    const toolOverflow = genomeToolOverflowCount(connectors, alfredOnOutreach);
    if (toolOverflow > 0) {
      tools.push({ id: 'TX', side: 'right', slot: 3, intoHub: false, label: '', connected: false, kind: 'overflow', overflowCount: toolOverflow, overflowTab: 'tools' });
    }

    return [...channels, ...tools];
  }, [connectors, t, alfredOnOutreach]);

  const onOverflow = (node: HeroNode) => {
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'genome_overflow', action: node.overflowTab });
    if (node.overflowTab === 'tools') onBrowseTools();
    else onBrowseChannels();
  };

  return (
    <div className={styles.genome}>
      <svg className={styles.genomeSvg} viewBox={GENOME_VIEWBOX} role="group" aria-label={t('connectors.title')}>
        {GENOME_RINGS.map((r, i) => (
          <circle key={i} className={styles.hRing} cx={GENOME_HUB.x} cy={GENOME_HUB.y} r={r.r} />
        ))}

        {dataSettled &&
          nodes.map((node) => <Spoke key={`s-${node.id}`} node={node} />)}
        {dataSettled &&
          nodes.map((node, i) => <Pulse key={`p-${node.id}`} node={node} stagger={i} />)}

        <HubMark />

        {dataSettled &&
          nodes.map((node, i) => <Badge key={node.id} node={node} stagger={i} onOverflow={onOverflow} />)}
      </svg>
    </div>
  );
}

/** The green Symphony app-icon hub: a lime squircle with the dark loop marks. */
function HubMark() {
  const rx = GENOME_MARK_SIZE * 0.3;
  return (
    <g>
      <rect className={styles.hHubTile} x={GENOME_HUB.x - GENOME_MARK_SIZE / 2} y={GENOME_HUB.y - GENOME_MARK_SIZE / 2} width={GENOME_MARK_SIZE} height={GENOME_MARK_SIZE} rx={rx} />
      <g transform={`translate(${GENOME_HUB.x},${GENOME_HUB.y}) scale(${GENOME_MARK_SIZE / 24}) translate(-12,-12)`}>
        {SYMPHONY_LOOP_MARKS.map((m, i) => (
          <rect key={i} className={styles.hHubMark} x={m.cx - m.size / 2} y={m.cy - m.size / 2} width={m.size} height={m.size} rx={m.rx} />
        ))}
      </g>
    </g>
  );
}

function Spoke({ node }: { node: HeroNode }) {
  const centre = genomeCentre(node.side, node.slot, node.kind === 'overflow');
  const { d } = genomeSpokePath(centre);
  return (
    <path
      className={`${styles.hSpoke} ${node.connected ? styles.hSpokeOn : styles.hSpokeOff}`}
      d={d}
      strokeDasharray={node.connected ? undefined : '2 2'}
    />
  );
}

/** The travelling signal: channels (LEFT) flow INTO the hub, tools (RIGHT) flow OUT of it. Every spoke
 *  path is authored badge → hub, so a channel runs it forward and a tool runs it in reverse. */
function Pulse({ node, stagger }: { node: HeroNode; stagger: number }) {
  const centre = genomeCentre(node.side, node.slot, node.kind === 'overflow');
  const { d } = genomeSpokePath(centre);
  const intoHub = node.side === 'left';
  return (
    <circle
      className={`${styles.hPulse} ${intoHub ? styles.hPulseInto : styles.hPulseOut}`}
      r={3}
      style={{ offsetPath: `path('${d}')`, animationDelay: `${GENOME_ARRIVAL_MS + stagger * GENOME_PULSE_STAGGER_MS}ms` } as React.CSSProperties}
    />
  );
}

/** The mark inside a badge — a brand glyph (mono ink), a real connector logo (colored), or the Wix
 *  wordmark — centred on the badge. */
function BadgeMark({ node }: { node: HeroNode }) {
  if (node.kind === 'overflow') {
    return (
      <text className={styles.hOverflowText} textAnchor="middle" dominantBaseline="central">
        {`+${node.overflowCount ?? 0}`}
      </text>
    );
  }
  const mark = node.mark;
  if (mark?.logoUrl) {
    const box = 22;
    return (
      <image
        href={mark.logoUrl}
        x={-box / 2}
        y={-box / 2}
        width={box}
        height={box}
        preserveAspectRatio="xMidYMid meet"
        onError={(e) => e.currentTarget.remove()}
      />
    );
  }
  if (mark?.wix) {
    const w = 24;
    return <g transform={`translate(${-w / 2},${-(w * 11.1995) / 28.9271 / 2})`}>{<WixWordmark width={w} />}</g>;
  }
  if (mark?.glyph) return <>{mark.glyph}</>;
  return null;
}

function Badge({ node, stagger, onOverflow }: { node: HeroNode; stagger: number; onOverflow: (n: HeroNode) => void }) {
  const overflow = node.kind === 'overflow';
  const c = genomeCentre(node.side, node.slot, overflow);
  const fly = {
    '--fx': `${(GENOME_HUB.x - c.x) * 0.55}px`,
    '--fy': `${(GENOME_HUB.y - c.y) * 0.55}px`,
    '--d': `${180 + stagger * 74}ms`,
  } as React.CSSProperties;

  const interactive = overflow
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `+${node.overflowCount ?? 0}`,
        onClick: () => onOverflow(node),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOverflow(node);
          }
        },
      }
    : {};

  const plateClass = overflow
    ? styles.hOverflowChip
    : node.connected
    ? styles.hPlateOn
    : styles.hPlateOff;

  return (
    <g transform={`translate(${c.x},${c.y})`} className={overflow ? styles.hBadgeTap : undefined} {...interactive}>
      <g className={styles.hBadgeFly} style={fly}>
        <circle className={plateClass} cx={0} cy={0} r={GENOME_BADGE_R} />
        <BadgeMark node={node} />
        {/* Every label sits above its badge in the horizontal layout. */}
        {node.label && (
          <text className={styles.hLabel} textAnchor="middle" y={-(GENOME_BADGE_R + 8)}>
            {node.label}
          </text>
        )}
      </g>
    </g>
  );
}
